/**
 * ============================================================================
 * USE WINDOW MANAGER HOOK
 * ============================================================================
 *
 * Core state management hook for the desktop window system.
 * Manages all window states, z-index ordering, and window actions.
 *
 * @packageDocumentation
 */

"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  UseWindowManagerReturn,
  WindowActions,
  WindowConfig,
  WindowGeometryState,
  WindowInstance,
  WindowState,
} from "../types";
import {
  createClosedState,
  createMinimizedState,
  createOpenState,
  isWindowMinimized,
  isWindowOpen,
} from "../types";
import {
  debounce,
  loadDesktopState,
  PersistedDesktopState,
  type PersistedWindowState,
  saveDesktopState,
} from "./windowStorage";

/* ==========================================================================
   HOOK IMPLEMENTATION
   ========================================================================== */

/**
 * Hook that manages all window state for a desktop environment.
 *
 * @param windowConfigs - Array of window configurations
 * @returns Window manager with state and actions for all windows
 *
 * @example
 * ```tsx
 * const windowConfigs = [
 *   { id: 'terminal', type: 'app', ... },
 *   { id: 'browser', type: 'browser', ... },
 * ];
 *
 * function Desktop() {
 *   const manager = useWindowManager(windowConfigs);
 *
 *   return (
 *     <>
 *       <button onClick={() => manager.openWindow('terminal')}>
 *         Open Terminal
 *       </button>
 *       {Array.from(manager.windows.values()).map(window => (
 *         <Window key={window.config.id} {...window} />
 *       ))}
 *     </>
 *   );
 * }
 * ```
 */
// Helper to get initial persisted state (called once during initialization)
function getInitialPersistedState(): PersistedDesktopState | null {
  return loadDesktopState();
}

export interface UseWindowManagerOptions {
  /**
   * Mobile app-switching semantics. When true, opening or restoring a
   * window first minimizes any currently-open windows so only one app
   * is foregrounded at a time (iOS-style). Irrelevant on desktop where
   * multiple windows co-exist.
   */
  isMobile?: boolean;
}

export function useWindowManager(
  windowConfigs: WindowConfig[],
  options: UseWindowManagerOptions = {}
): UseWindowManagerReturn {
  const { isMobile = false } = options;
  // Read through a ref so the memoized action callbacks pick up the
  // latest viewport-derived value without re-creating on every toggle.
  const isMobileRef = useRef(isMobile);
  isMobileRef.current = isMobile;
  // Load persisted state once during initialization
  // We use a ref to store the result so it's only loaded once
  const initialPersistedStateRef = useRef<
    PersistedDesktopState | null | undefined
  >(undefined);
  if (initialPersistedStateRef.current === undefined) {
    initialPersistedStateRef.current = getInitialPersistedState();
  }
  const initialPersistedState = initialPersistedStateRef.current;

  // Track the next z-index to assign (using ref to avoid stale closures).
  // Capped to stay well below UI chrome z-indexes (dock=50, menubar=100).
  // Window z-indexes only need to express relative order between open windows.
  const MAX_Z_INDEX = 30;
  const nextZIndexRef = useRef(
    Math.min(initialPersistedState?.nextZIndex ?? 1, MAX_Z_INDEX)
  );

  /**
   * Re-number z-indexes of all open/minimized windows to compact values
   * starting from 1, preserving relative order. Called when the counter
   * grows too large so windows never overlap UI chrome.
   */
  const normalizeZIndexes = useCallback(
    (states: Map<string, WindowState>): Map<string, WindowState> => {
      // Collect windows that have z-indexes, sorted by current z-index
      const entries: { id: string; state: WindowState; z: number }[] = [];
      for (const [id, state] of states) {
        if (
          (isWindowOpen(state) || isWindowMinimized(state)) &&
          "zIndex" in state
        ) {
          entries.push({ id, state, z: state.zIndex });
        }
      }
      entries.sort((a, b) => a.z - b.z);

      const next = new Map(states);
      entries.forEach((entry, i) => {
        const newZ = i + 1;
        if (isWindowOpen(entry.state)) {
          next.set(entry.id, { ...entry.state, zIndex: newZ });
        } else if (isWindowMinimized(entry.state)) {
          next.set(entry.id, { ...entry.state, zIndex: newZ });
        }
      });
      nextZIndexRef.current = entries.length + 1;
      return next;
    },
    []
  );

  // Geometry states for each window (position, size, snap state)
  const [geometryStates, setGeometryStates] = useState<
    Map<string, WindowGeometryState>
  >(() => {
    const initial = new Map<string, WindowGeometryState>();
    const persisted = initialPersistedState;

    for (const config of windowConfigs) {
      if (persisted?.windows[config.id]) {
        const pw = persisted.windows[config.id];
        initial.set(config.id, {
          position: pw.position,
          size: pw.size,
          savedPosition: pw.savedPosition,
          savedSize: pw.savedSize,
          previousGeometry: pw.previousGeometry,
          isSnapped: pw.isSnapped,
        });
      }
    }
    return initial;
  });

  // State for all windows - keyed by window ID
  const [windowStates, setWindowStates] = useState<Map<string, WindowState>>(
    () => {
      const initial = new Map<string, WindowState>();
      const persisted = initialPersistedState;

      for (const config of windowConfigs) {
        // Check if we have persisted state for this window
        if (persisted?.windows[config.id]) {
          const pw = persisted.windows[config.id];
          if (pw.status === "open") {
            initial.set(
              config.id,
              createOpenState(
                pw.isActive ?? false,
                pw.isMaximized ?? false,
                Math.min(pw.zIndex ?? 1, MAX_Z_INDEX)
              )
            );
          } else if (pw.status === "minimized") {
            initial.set(
              config.id,
              createMinimizedState(Math.min(pw.zIndex ?? 1, MAX_Z_INDEX))
            );
          } else {
            initial.set(config.id, createClosedState());
          }
        } else {
          initial.set(config.id, createClosedState());
        }
      }
      return initial;
    }
  );

  // Update geometry for a window (called by Window component)
  const updateGeometry = useCallback(
    (windowId: string, geometry: Partial<WindowGeometryState>) => {
      setGeometryStates((prev) => {
        const next = new Map(prev);
        const current = prev.get(windowId) ?? {};
        next.set(windowId, { ...current, ...geometry });
        return next;
      });
    },
    []
  );

  // Debounced save function
  const debouncedSave = useMemo(
    () =>
      debounce(
        (
          states: Map<string, WindowState>,
          geometries: Map<string, WindowGeometryState>,
          nextZ: number
        ) => {
          const windows: Record<string, PersistedWindowState> = {};

          for (const [id, state] of states) {
            const geometry = geometries.get(id);

            if (isWindowOpen(state)) {
              windows[id] = {
                status: "open",
                isActive: state.isActive,
                isMaximized: state.isMaximized,
                zIndex: state.zIndex,
                position: geometry?.position,
                size: geometry?.size,
                savedPosition: geometry?.savedPosition,
                savedSize: geometry?.savedSize,
                previousGeometry: geometry?.previousGeometry,
                isSnapped: geometry?.isSnapped,
              };
            } else if (isWindowMinimized(state)) {
              windows[id] = {
                status: "minimized",
                zIndex: state.zIndex,
                position: geometry?.position,
                size: geometry?.size,
                savedPosition: geometry?.savedPosition,
                savedSize: geometry?.savedSize,
                previousGeometry: geometry?.previousGeometry,
                isSnapped: geometry?.isSnapped,
              };
            } else {
              // Keep geometry for closed windows so they restore to same position
              if (geometry?.position || geometry?.size) {
                windows[id] = {
                  status: "closed",
                  position: geometry?.position,
                  size: geometry?.size,
                  savedPosition: geometry?.savedPosition,
                  savedSize: geometry?.savedSize,
                  previousGeometry: geometry?.previousGeometry,
                  isSnapped: geometry?.isSnapped,
                };
              }
            }
          }

          saveDesktopState({ windows, nextZIndex: nextZ });
        },
        100
      ),
    []
  );

  // Sync windowStates when windowConfigs change (e.g., when auth loads and
  // new windows become visible). Add any missing windows as closed.
  useEffect(() => {
    setWindowStates((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const config of windowConfigs) {
        if (!next.has(config.id)) {
          next.set(config.id, createClosedState());
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [windowConfigs]);

  // Save state whenever it changes
  useEffect(() => {
    debouncedSave(windowStates, geometryStates, nextZIndexRef.current ?? 1);
  }, [windowStates, geometryStates, debouncedSave]);

  /* ========================================================================
     WINDOW ACTIONS
     ======================================================================== */

  /**
   * Open a window by ID.
   */
  const openWindow = useCallback(
    (id: string) => {
      setWindowStates((prev) => {
        const current = prev.get(id);
        if (!current) {
          console.warn(`useWindowManager: Window "${id}" not found`);
          return prev;
        }

        // Already open? Just focus it
        if (isWindowOpen(current)) {
          return prev;
        }

        // Normalize z-indexes if counter is getting too high
        const next =
          nextZIndexRef.current >= MAX_Z_INDEX
            ? normalizeZIndexes(prev)
            : new Map(prev);

        const mobile = isMobileRef.current;
        // Deactivate other windows. On mobile we also minimize them so
        // the old foreground app is reachable from the app switcher
        // (iOS-style single-foreground behavior). Skip the window being
        // opened itself so it doesn't get minimized before promotion.
        for (const [windowId, state] of next) {
          if (windowId === id || !isWindowOpen(state)) continue;
          if (mobile) {
            next.set(windowId, createMinimizedState(state.zIndex));
          } else if (state.isActive) {
            next.set(windowId, { ...state, isActive: false });
          }
        }

        // Open this window with highest z-index
        const zIndex = nextZIndexRef.current++;
        next.set(id, createOpenState(true, false, zIndex));

        return next;
      });
    },
    [normalizeZIndexes]
  );

  /**
   * Close a window by ID.
   */
  const closeWindow = useCallback((id: string) => {
    setWindowStates((prev) => {
      const current = prev.get(id);
      if (!current) {
        console.warn(`useWindowManager: Window "${id}" not found`);
        return prev;
      }

      const next = new Map(prev);
      next.set(id, createClosedState());
      return next;
    });
  }, []);

  /**
   * Minimize a window by ID.
   */
  const minimizeWindow = useCallback((id: string) => {
    setWindowStates((prev) => {
      const current = prev.get(id);
      if (!current || !isWindowOpen(current)) {
        return prev;
      }

      const next = new Map(prev);
      next.set(id, createMinimizedState(current.zIndex));
      return next;
    });
  }, []);

  /**
   * Restore a minimized window by ID.
   */
  const restoreWindow = useCallback(
    (id: string) => {
      setWindowStates((prev) => {
        const current = prev.get(id);
        if (!current || !isWindowMinimized(current)) {
          return prev;
        }

        const next =
          nextZIndexRef.current >= MAX_Z_INDEX
            ? normalizeZIndexes(prev)
            : new Map(prev);

        const mobile = isMobileRef.current;
        // Same single-foreground rule as openWindow on mobile:
        // restoring from the app switcher pushes the previously-open
        // app into the switcher so the user can pop back.
        for (const [windowId, state] of next) {
          if (windowId === id || !isWindowOpen(state)) continue;
          if (mobile) {
            next.set(windowId, createMinimizedState(state.zIndex));
          } else if (state.isActive) {
            next.set(windowId, { ...state, isActive: false });
          }
        }

        // Restore with new highest z-index
        const zIndex = nextZIndexRef.current++;
        next.set(id, createOpenState(true, false, zIndex));

        return next;
      });
    },
    [normalizeZIndexes]
  );

  /**
   * Focus/activate a window by ID (bring to front).
   */
  const focusWindow = useCallback(
    (id: string) => {
      setWindowStates((prev) => {
        const current = prev.get(id);
        if (!current || !isWindowOpen(current)) {
          return prev;
        }

        // Already active and at top? No change needed
        if (current.isActive) {
          return prev;
        }

        const next =
          nextZIndexRef.current >= MAX_Z_INDEX
            ? normalizeZIndexes(prev)
            : new Map(prev);

        // Deactivate all other windows
        for (const [windowId, state] of next) {
          if (isWindowOpen(state) && state.isActive) {
            next.set(windowId, { ...state, isActive: false });
          }
        }

        // Re-read current from normalized map
        const normalizedCurrent = next.get(id);
        if (!normalizedCurrent || !isWindowOpen(normalizedCurrent)) return prev;

        // Activate this window with highest z-index
        const zIndex = nextZIndexRef.current++;
        next.set(id, {
          ...normalizedCurrent,
          isActive: true,
          zIndex,
        });

        return next;
      });
    },
    [normalizeZIndexes]
  );

  /**
   * Deactivate all windows (e.g., when clicking desktop background).
   */
  const deactivateAll = useCallback(() => {
    setWindowStates((prev) => {
      let hasActive = false;
      for (const state of prev.values()) {
        if (isWindowOpen(state) && state.isActive) {
          hasActive = true;
          break;
        }
      }

      if (!hasActive) return prev;

      const next = new Map(prev);
      for (const [windowId, state] of next) {
        if (isWindowOpen(state) && state.isActive) {
          next.set(windowId, { ...state, isActive: false });
        }
      }
      return next;
    });
  }, []);

  /**
   * Close all windows and reset z-index counter (e.g., on logout).
   */
  const closeAllWindows = useCallback(() => {
    setWindowStates((prev) => {
      const next = new Map(prev);
      for (const [id] of next) {
        next.set(id, createClosedState());
      }
      return next;
    });
    setGeometryStates(new Map());
    nextZIndexRef.current = 1;
  }, []);

  /* ========================================================================
     BUILD WINDOW INSTANCES
     ======================================================================== */

  /**
   * Create actions object for a specific window.
   */
  const createActions = useCallback(
    (windowId: string): WindowActions => ({
      open: () => openWindow(windowId),
      close: () => closeWindow(windowId),
      minimize: () => minimizeWindow(windowId),
      restore: () => restoreWindow(windowId),
      maximize: () => {
        setWindowStates((prev) => {
          const current = prev.get(windowId);
          if (!current || !isWindowOpen(current) || current.isMaximized)
            return prev;
          const next = new Map(prev);
          next.set(windowId, { ...current, isMaximized: true });
          return next;
        });
      },
      unmaximize: () => {
        setWindowStates((prev) => {
          const current = prev.get(windowId);
          if (!current || !isWindowOpen(current) || !current.isMaximized)
            return prev;
          const next = new Map(prev);
          next.set(windowId, { ...current, isMaximized: false });
          return next;
        });
      },
      toggleMaximize: () => {
        setWindowStates((prev) => {
          const current = prev.get(windowId);
          if (!current || !isWindowOpen(current)) return prev;
          const next = new Map(prev);
          next.set(windowId, { ...current, isMaximized: !current.isMaximized });
          return next;
        });
      },
      focus: () => focusWindow(windowId),
    }),
    [openWindow, closeWindow, minimizeWindow, restoreWindow, focusWindow]
  );

  /**
   * Build the complete windows Map with config, state, and actions.
   */
  const windows = useMemo(() => {
    const result = new Map<string, WindowInstance>();

    for (const config of windowConfigs) {
      const state = windowStates.get(config.id) || createClosedState();
      const actions = createActions(config.id);

      result.set(config.id, {
        config,
        state,
        actions,
      });
    }

    return result;
  }, [windowConfigs, windowStates, createActions]);

  /* ========================================================================
     RETURN VALUE
     ======================================================================== */

  const clearGeometry = useCallback((windowId: string) => {
    setGeometryStates((prev) => {
      const next = new Map(prev);
      next.delete(windowId);
      return next;
    });
  }, []);

  return {
    windows,
    openWindow,
    closeWindow,
    minimizeWindow,
    restoreWindow,
    focusWindow,
    deactivateAll,
    closeAllWindows,
    geometryStates,
    updateGeometry,
    clearGeometry,
  };
}
